import { FC, useState, useEffect, useCallback, startTransition } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";

import { Pagination } from "./components/Pagination";
import { SourceTableProps, SourceUsageTableRow } from "./types";
import { getSourceTableColumns } from "./sourceTableColumns";

import styles from "./SourceTable.module.css";



const DEFAULT_PAGE_SIZE = 20;

const COLUMNS_WITH_DOMAIN_TYPE = getSourceTableColumns(styles, {
  showDomainType: true,
});
const COLUMNS_WITHOUT_DOMAIN_TYPE = getSourceTableColumns(styles, {
  showDomainType: false,
});

const SourceTable: FC<SourceTableProps> = ({
  className,
  rows = [],
  showDomainTypeColumn = true,
  disableLinks = false,
  pageSize: pageSizeProp,
  totalRows: totalRowsProp,
  currentPage: currentPageProp,
  onPageChange: onPageChangeProp,
}) => {
  const [page, setPage] = useState(1);
  const pageSize = Math.max(1, pageSizeProp ?? DEFAULT_PAGE_SIZE);
  const isServerSide =
    totalRowsProp != null &&
    currentPageProp != null &&
    typeof onPageChangeProp === "function";

  const totalRows = isServerSide ? totalRowsProp : rows.length;
  const showPagination = totalRows > pageSize;
  const totalPages = showPagination
    ? Math.ceil(totalRows / pageSize)
    : 1;
  const safePage = isServerSide
    ? Math.min(Math.max(1, currentPageProp), totalPages)
    : Math.min(Math.max(1, page), totalPages);
  const startIndex = showPagination && !isServerSide ? (safePage - 1) * pageSize : 0;
  const endIndex =
    showPagination && !isServerSide
      ? Math.min(startIndex + pageSize, totalRows)
      : totalRows;
  const displayRows = isServerSide
    ? rows
    : showPagination
      ? rows.slice(startIndex, endIndex)
      : rows;

  useEffect(() => {
    if (!isServerSide) setPage(1);
  }, [isServerSide, rows.length]);

  useEffect(() => {
    if (!isServerSide && showPagination && page > totalPages) {
      setPage(totalPages);
    }
  }, [isServerSide, showPagination, page, totalPages]);

  let columns = showDomainTypeColumn
    ? COLUMNS_WITH_DOMAIN_TYPE
    : COLUMNS_WITHOUT_DOMAIN_TYPE;
  if (disableLinks) {
    columns = columns.filter((col) => col.id !== "link");
  }
  const table = useReactTable({
    data: displayRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const navigate = useNavigate();
  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  const handleRowClick = (row: SourceUsageTableRow) =>
    disableLinks ? undefined : () => navigate(row.detailHref);

  const handlePageChange = useCallback(
    (pageNum: number) => {
      if (isServerSide && onPageChangeProp) {
        onPageChangeProp(pageNum);
      } else {
        startTransition(() => setPage(pageNum));
      }
    },
    [isServerSide, onPageChangeProp]
  );

  return (
    <div className={styles["source_table_wrapper"]}>
      <table
        className={classNames(
          styles["source_table"],
          className
        )}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className={styles["source_table__header_row"]}
            >
              {headerGroup.headers.map((header) => (
                <th key={header.id} >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr
              key={row.id}
              className={classNames(
                styles["source_table__body_row"],
                !disableLinks && styles["source_table__body_row_clickable"]
              )}
              onClick={handleRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {showPagination && (
        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default SourceTable;
